/**
 * My offers — the signed-in buyer's own offers (web).
 *
 * Lists every offer the buyer has placed, across all listings, via the authed
 * GET /marketplace/offers. The endpoint returns a { data: [...] } envelope whose
 * rows carry listing-context fields (product / sellerName / askPriceZarpPerKgMicro
 * / listingStatus) alongside the offer itself, so a buyer can read each row
 * without a second fetch. Hand-rolled client (src/api/marketplace.ts — orval not
 * yet regenerated for /marketplace).
 *
 * A pending offer can be WITHDRAWN (PATCH /marketplace/offers/:id/withdraw);
 * accepted / rejected / withdrawn / expired offers are terminal and show no
 * action. Money is micro-ZARP (1e-6 ZARP) STRINGS; we convert the offered
 * price-per-kg + the total with microToZarp() and render with the @fse/ui
 * <Money>. The nav item is role-gated to `buyer` in _app.tsx (central wiring).
 * i18n keys live under myOffers.* in all four web locales.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Money,
  Stack,
  Text,
  type ChipColor,
} from '@fse/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError } from '../api/client';
import {
  listMyOffers,
  microToZarp,
  withdrawOffer,
  type OfferDto,
  type OfferStatus,
} from '../api/marketplace';

/** Map an offer lifecycle state → kit `Chip` colour (assist chip as a badge). */
const STATUS_CHIP: Record<OfferStatus, ChipColor> = {
  pending: 'tertiary',
  accepted: 'success',
  rejected: 'alert',
  withdrawn: 'neutral',
  expired: 'neutral',
};

function formatTs(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export function MyOffersScreen() {
  const { t } = useTranslation();

  const offersQuery = useQuery({
    queryKey: ['my-offers'],
    queryFn: ({ signal }) => listMyOffers(signal),
  });

  const offers = offersQuery.data?.data ?? [];

  return (
    <Stack spacing={3}>
      <Box>
        <Text variant="headline-medium" as="h2">
          {t('myOffers.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {offersQuery.data
              ? t('myOffers.subtitleWithCount', { count: offers.length })
              : t('myOffers.subtitle')}
          </Text>
        </Box>
      </Box>

      {offersQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : offersQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => offersQuery.refetch()}>
              {t('myOffers.retry')}
            </Button>
          }
        >
          {t('myOffers.loadError', {
            error:
              offersQuery.error instanceof FseApiError
                ? offersQuery.error.message
                : String(offersQuery.error),
          })}
        </Alert>
      ) : offers.length === 0 ? (
        <Card>
          <CardContent>
            <Stack spacing={1}>
              <Text variant="title-medium" as="h3">
                {t('myOffers.empty.title')}
              </Text>
              <Text variant="body-medium" color="text.secondary">
                {t('myOffers.empty.body')}
              </Text>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {offers.map((offer) => (
            <OfferCard key={offer.id} offer={offer} t={t} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

interface OfferCardProps {
  offer: OfferDto;
  t: TFunction;
}

function OfferCard({ offer, t }: OfferCardProps) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const offerPerKg = microToZarp(offer.offerPriceZarpPerKgMicro);
  const total = microToZarp(offer.totalZarpMicro);
  const askPerKg =
    offer.askPriceZarpPerKgMicro != null ? microToZarp(offer.askPriceZarpPerKgMicro) : null;
  const isPending = offer.status === 'pending';

  const withdrawMutation = useMutation({
    mutationFn: () => withdrawOffer(offer.id),
    onSuccess: () => {
      setConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ['my-offers'] });
    },
  });

  function closeConfirm() {
    if (withdrawMutation.isPending) return;
    withdrawMutation.reset();
    setConfirmOpen(false);
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          {/* Header: product + status chip */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Text variant="title-medium" as="span">
              {offer.product ?? t('myOffers.unknownProduct')}
            </Text>
            <Chip
              kind="assist"
              color={STATUS_CHIP[offer.status]}
              label={t(`myOffers.status.${offer.status}`)}
            />
          </Box>

          {/* Seller + listing status */}
          <Text variant="label-medium" color="text.secondary">
            {t('myOffers.seller', { name: offer.sellerName ?? t('myOffers.unknownSeller') })}
          </Text>

          {/* Money grid: your offer / total / seller ask */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            <Box>
              <Text variant="label-medium" color="text.secondary">
                {t('myOffers.yourOffer')}
              </Text>
              <Box sx={{ mt: 0.25 }}>
                <Money amount={offerPerKg} unit={t('myOffers.perKg')} size="md" />
              </Box>
            </Box>
            <Box>
              <Text variant="label-medium" color="text.secondary">
                {t('myOffers.total')}
              </Text>
              <Box sx={{ mt: 0.25 }}>
                <Money amount={total} size="md" />
              </Box>
            </Box>
            {askPerKg != null ? (
              <Box>
                <Text variant="label-medium" color="text.secondary">
                  {t('myOffers.askPrice')}
                </Text>
                <Box sx={{ mt: 0.25 }}>
                  <Money amount={askPerKg} unit={t('myOffers.perKg')} size="md" />
                </Box>
              </Box>
            ) : null}
          </Box>

          {/* Quantity + placed-at */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Text variant="label-small" color="text.secondary">
              {t('myOffers.qtyKg', { qty: offer.qtyKg })}
            </Text>
            <Text variant="label-small" color="text.secondary">
              {t('myOffers.placedAt', { when: formatTs(offer.createdAt) })}
            </Text>
          </Box>

          {/* Withdraw action — only while pending */}
          {isPending ? (
            <Box>
              <Button variant="outlined" onClick={() => setConfirmOpen(true)}>
                {t('myOffers.withdraw')}
              </Button>
            </Box>
          ) : null}
        </Stack>
      </CardContent>

      {/* Withdraw confirm dialog */}
      <Dialog open={confirmOpen} onClose={closeConfirm} fullWidth maxWidth="sm">
        <DialogTitle>{t('myOffers.withdrawConfirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('myOffers.withdrawConfirm.body')}</DialogContentText>
          {withdrawMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t('myOffers.withdrawConfirm.error', {
                error:
                  withdrawMutation.error instanceof FseApiError
                    ? withdrawMutation.error.message
                    : String(withdrawMutation.error),
              })}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={closeConfirm} disabled={withdrawMutation.isPending}>
            {t('myOffers.withdrawConfirm.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => withdrawMutation.mutate()}
            disabled={withdrawMutation.isPending}
            startIcon={withdrawMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t('myOffers.withdrawConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
